// Builds a print-ready payout invoice (Cherry Picked → Professional)
// and opens it in a new tab. Used from the Payouts page and the pro
// booking detail page — both surfaces want the same breakdown:
// gross customer payment, platform fee, net to the pro.
//
// No backend round-trip — the page generates the HTML inline and
// calls `window.print()` so the pro saves it as PDF via the browser
// print dialog.

export type ProPayoutInvoiceInput = {
  reference: string | null | undefined;
  scheduled_start?: string | null;
  payout_date?: string | null;
  currency: string;
  gross_amount: number;
  platform_fee_percentage: number;
  platform_fee_amount: number;
  net_amount: number;
  // Optional context that makes the invoice readable when the data
  // is at hand (booking detail). Omitted on the payouts table where
  // we only have the row.
  service_name?: string | null;
  category_name?: string | null;
  customer_name?: string | null;
  pro_name?: string | null;
  pro_business_name?: string | null;
  address?: string | null;
  status?: string | null;
  is_finalised?: boolean;
};

function escapeHtml(s: string | null | undefined): string {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function formatMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);
  } catch {
    return `${currency} ${Number(amount).toFixed(2)}`;
  }
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

export function downloadProPayoutInvoice(input: ProPayoutInvoiceInput) {
  const currency = input.currency || 'GBP';
  const m = (n: number) => formatMoney(Number(n || 0), currency);
  const heading = input.is_finalised === false
    ? 'Payout estimate'
    : 'Payout invoice';
  const proLine =
    input.pro_business_name && input.pro_name
      ? `${input.pro_business_name} · ${input.pro_name}`
      : input.pro_business_name || input.pro_name || '';
  const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>${escapeHtml(heading)} ${escapeHtml(input.reference ?? '')}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif; color: #3a3335; margin: 40px; }
  .head { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #8e2a2f; padding-bottom: 16px; margin-bottom: 24px; }
  .brand { font-family: Georgia, serif; font-weight: 800; font-size: 28px; color: #8e2a2f; letter-spacing: -.02em; }
  .muted { color: #908e8e; font-size: 12px; }
  h2 { font-size: 13px; color: #8e2a2f; text-transform: uppercase; letter-spacing: .08em; margin: 24px 0 8px; }
  .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; font-size: 13px; }
  .info-grid p { margin: 4px 0; }
  .info-grid strong { color: #3a3335; }
  table { width: 100%; border-collapse: collapse; font-size: 14px; }
  td { padding: 10px 12px; }
  .row { border-bottom: 1px solid #ede8e9; }
  .label { color: #908e8e; }
  .num { text-align: right; white-space: nowrap; }
  .total { font-size: 18px; font-weight: 700; }
  .total td { padding-top: 14px; border-top: 2px solid #3a3335; color: #8e2a2f; }
  .footer { margin-top: 40px; font-size: 11px; color: #908e8e; border-top: 1px solid #ede8e9; padding-top: 12px; }
  .pill { display: inline-block; padding: 2px 10px; border-radius: 999px; font-size: 11px; font-weight: 700; background: #fcfaf7; border: 1px solid #ede8e9; color: #3a3335; }
  @media print {
    body { margin: 20mm; }
    .no-print { display: none; }
  }
</style>
</head>
<body>
  <div class="head">
    <div>
      <div class="brand">Cherry Picked</div>
      <div class="muted">${escapeHtml(heading)}</div>
    </div>
    <div style="text-align:right">
      ${
        input.reference
          ? `<div style="font-size:14px;font-weight:700">Ref ${escapeHtml(input.reference)}</div>`
          : ''
      }
      <div class="muted">Issued: ${new Date().toLocaleDateString()}</div>
    </div>
  </div>

  <div class="info-grid">
    ${
      proLine
        ? `<div><p><strong>Paid to</strong></p><p>${escapeHtml(proLine)}</p></div>`
        : ''
    }
    ${
      input.customer_name
        ? `<div><p><strong>Customer</strong></p><p>${escapeHtml(input.customer_name)}</p></div>`
        : ''
    }
    ${
      input.service_name
        ? `<div><p><strong>Service</strong></p><p>${escapeHtml(input.service_name)}</p>${input.category_name ? `<p class="muted">${escapeHtml(input.category_name)}</p>` : ''}</div>`
        : ''
    }
    ${
      input.scheduled_start
        ? `<div><p><strong>Booking date</strong></p><p>${formatDateTime(input.scheduled_start)}</p></div>`
        : ''
    }
    ${
      input.payout_date
        ? `<div><p><strong>Payout date</strong></p><p>${formatDateTime(input.payout_date)}</p></div>`
        : ''
    }
    ${
      input.address
        ? `<div><p><strong>Address</strong></p><p>${escapeHtml(input.address)}</p></div>`
        : ''
    }
  </div>

  <h2>Earnings breakdown</h2>
  <table>
    <tr class="row">
      <td class="label">Customer paid (gross)</td>
      <td class="num">${m(input.gross_amount)}</td>
    </tr>
    <tr class="row">
      <td class="label">Cherry Picked platform fee (${Number(input.platform_fee_percentage)}%)</td>
      <td class="num">− ${m(input.platform_fee_amount)}</td>
    </tr>
    <tr class="total">
      <td>${input.is_finalised === false ? 'You will receive (after fee)' : 'You receive (after fee)'}</td>
      <td class="num">${m(input.net_amount)}</td>
    </tr>
  </table>

  <p class="footer">
    Amount paid out to you is net of the ${Number(input.platform_fee_percentage)}% Cherry Picked
    platform fee shown above.
    ${
      input.is_finalised === false
        ? ' Estimated breakdown — the final amount is locked in when the booking is marked completed. Payouts are batched on the next fortnightly cycle.'
        : ' Payout settled via Stripe Connect. Keep this receipt for your records.'
    }
  </p>
  <script>window.onload = function(){ setTimeout(function(){ window.print(); }, 250); };</script>
</body>
</html>`;

  const w = window.open('', '_blank');
  if (!w) {
    window.location.href = `data:text/html;charset=utf-8,${encodeURIComponent(html)}`;
    return;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
}
